var _ = require('underscore');
var TorsoFormView = require('backbone-torso/modules/FormView');
var AuthModel = require('./AuthModel');
var authFormTemplate = require('./authFormTemplate.hbs');
var userService = require('./../user/userService');
var LogoutIfLoggedInBehavior = require('./LogoutIfLoggedInBehavior');
var applicationUtils = require('./../applicationUtils');
var capslockDetectorUtils = require('./../utils/capslockDetectorUtils');

var AUTH_CONTEXTS = {
  PORTAL_PATIENT: {
    name: 'PORTAL_PATIENT',
    isPatient: true,
    isEreg: false,
    permissionRole: 'portal.patient'
  },
  ADMIN_TOOL: {
    name: 'ADMIN_TOOL',
    isPatient: false,
    isEreg: false,
    permissionRole: 'kiosk.admin'
  },
  DATA_GENERATOR: {
    name: 'DATA_GENERATOR',
    isPatient: false,
    isEreg: false,
    permissionRole: 'data.generator.user'
  },
  EREG_PATIENT: {
    name: 'EREG_PATIENT',
    isPatient: true,
    isEreg: true,
    permissionRole: 'portal.patient'
  },
  EREG_STAFF: {
    name: 'EREG_STAFF',
    isPatient: false,
    isEreg: true,
    permissionRole: 'portal.admin'
  }
};

/**
 * Form view for authentication
 *
 * @class AuthFormView
 * @constructor
 * @param options {Object}
 *   @param [options.isPatient=true] {Boolean} whether the authentication is for a patient (true) or admin (false).
 *   @param [options.permissionRole='portal.patient'] {String} the permission that the user needs to successfully authenticate.
 *   @param [options.navigateToPageFn=_.noop] {Function} a function that is run when authentication is successful.
 *   @param [options.showHelp=true] {Boolean} should a link to login help be shown? (true - show it, false don't show it)
 *   @param [options.authContext=AUTH_CONTEXTS.PORTAL_PATIENT] {String} the auth context to render in.
 */
var AuthFormView = TorsoFormView.extend({
  tagName: 'main',
  className: 'brec-authentication__main',
  template: authFormTemplate,

  AUTH_CONTEXTS: AUTH_CONTEXTS,

  /**
   * Map from error code to errors object to use.  If a function is defined then it will be
   * called in the context of this view.
   * @property _errorCodeMap
   * @type {Object}
   */
  _errorCodeMap: {
    401 : { errorCode: 'BAD_CREDENTIALS' },
    403 : { errorCode: 'UNAUTHORIZED' },
    unknown : { errorCode: 'ERROR' }
  },

  events: {
    'submit': '_submit'
  },

  /**
   * @property authValidation {Object} the set of validation rules required to authenticate.
   */
  authValidation: {
    username: {
      required: true
    },
    password: {
      required: true
    }
  },

  behaviors: {
    logoutIfLoggedIn: LogoutIfLoggedInBehavior
  },

  feedback: [{
    //******** Username *********
    /**
     * On keyup or blur on the username or submits, validate the username
     * @property feedback[0] (Username Error Message) {When/Then Feedback}
     */
    when: {
      '@username' : ['keyup', 'blur'],
      'on': ['validate']
    },
    then: function() {
      var isValid = this.model.isValid('username');
      if (!isValid) {
        return {
          text: $.i18n.prop('auth.error.username'),
          addClass: 'brec-feedback--error'
        };
      } else {
        return {
          text: '',
          removeClass: 'brec-feedback--error'
        };
      }
    },
    to: 'usernameError'
  }, {
    /**
     * On keyup or blur on the username or submits, validate the username
     * @property feedback[1] (Username Input Field) {When/Then Feedback}
     */
    when: {
      '@username' : ['keyup', 'blur'],
      'on': ['validate']
    },
    then: function() {
      return this._thenAddClassIfInvalid('username', 'brec-form__input--error');
    },
    to: 'username'
  }, {
    //******** Password *********
    /**
     * On keyup or blur on the password or submits, validate the password
     * @property feedback[2] (Password Error Message) {When/Then Feedback}
     */
    when: {
      '@password' : ['keyup', 'blur'],
      'on': ['validate']
    },
    then: function() {
      var isValid = this.model.isValid('password');
      if (!isValid) {
        return {
          text: $.i18n.prop('auth.error.password'),
          addClass: 'brec-feedback--error'
        };
      } else {
        return {
          text: '',
          removeClass: 'brec-feedback--error'
        };
      }
    },
    to: 'passwordError'
  }, {
    /**
     * On keyup or blur on the password or submits, validate the password
     * @property feedback[3] (Password Input Field) {When/Then Feedback}
     */
    when: {
      '@password' : ['keyup', 'blur'],
      'on': ['validate']
    },
    then: function() {
      return this._thenAddClassIfInvalid('password', 'brec-form__input--error');
    },
    to: 'password'
  }, {
    /**
    * On keypress on the password, check if the Capslock is on
    * @property feedback[4] (Password Capslock On Warning) {When/Then Feedback}
    */
    when: {
      '@password' : ['keypress']
    },
    then: function(event) {
      if (!capslockDetectorUtils.canCapsLockBeDetected(event)) {
        return;
      }
      if (capslockDetectorUtils.isCapsLockOn(event)) {
        return {
          removeClass: 'brec-hidden',
          attr: ['aria-hidden', 'false']
        };
      } else {
        return {
          addClass: 'brec-hidden',
          attr: ['aria-hidden', 'true']
        };
      }
    },
    to: 'passwordCapslockOnWarning'
  }],

  /**
   * @method initialize
   * @param options {Object}
   *   @param [options.authContext=AUTH_CONTEXTS.PORTAL_PATIENT] {String} the auth context to render in.
   *   @param [options.navigateToPageFn=_.noop] {Function} a function that is run when authentication is successful.
   * @override
   */
  initialize: function(options) {
    var authContext = options.authContext || AUTH_CONTEXTS.PORTAL_PATIENT;
    this.set('authContext', authContext);
    // Bind this context to methods used by promises
    _.bindAll(this, '_processAuthErrors', '_processAuthResponse', 'render');

    // Set up the authentication model
    this._authModel = new AuthModel({ permissionRole: authContext.permissionRole });
    this.model.setMapping('auth', _.keys(this.authValidation).join(' '), this._authModel);
    this.model.validation = this.authValidation;

    this._navigateToPageFn = options.navigateToPageFn || this._navigateToPageFn;

    this.listenTo(this.viewState, 'change', this.render);

  },

  /**
   * Default navigate to use if navigateToPageFn is not passed in.
   * @method _navigateToPageFn
   * @private
   */
  _navigateToPageFn: _.noop,

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var templateContext = TorsoFormView.prototype.prepare.apply(this);
    templateContext.currentApplication = applicationUtils.currentApplication;
    templateContext.AUTH_CONTEXTS = AUTH_CONTEXTS;
    return templateContext;
  },

  /**
   * Clears the data on the form and then rerenders.
   * @private
   * @method _clearFormData
   */
  _clearFormData: function() {
    // Weird validation bug occurs here which prevents the model from clearing. Need to set silent to true to prevent that.
    // Issue is that in backbone-nested this will attempt to validate the object and only clear the object if the
    // validation fails with errors to return. Passing silent: true will bypass the if condition that is doing the validation.
    this.model.clear({silent: true});
    this.render();
  },

  // ----- Event Triggered Methods -------------------------------------------------------------------------------------------------

  /**
   * Actions to perform when the user submits the page.
   *
   * @param event {Object} the event that triggered this.
   * @private
   * @method submit
   */
  _submit: function(event) {
    event.preventDefault();
    if (!this.get('submitting')) {
      this.trigger('validate');

      var validationErrors = this.model.validate();
      if (validationErrors) {
        this.set('errors', validationErrors);
      } else {
        this.set('submitting', true);
        this.set('errors', []);

        // Push the form data back to the _authModel and then authenticate
        this.model.push();

        this._authModel.authenticate()
          .then(this._processAuthResponse)
          .then(userService.resetAndFetchModels)
          .then(this._navigateToPageFn, this._processAuthErrors);
      }
    }
  },

  // ----- Helpers -----------------------------------------------------------------------------------------------------------------

  /**
   * Called on authentication success. Clears out models and forwards the user to the dashboard page.
   *
   * @private
   * @method _processAuthResponse
   */
  _processAuthResponse: function(data, status, headers) {
    userService.set('authenticated', true);
    if (headers.getResponseHeader('Password-Expired')) {
      var fullUrl = window.location.href;
      var origin = window.location.origin;
      var relativeUrl = fullUrl.replace(origin, '');
      relativeUrl = encodeURIComponent(relativeUrl);
      window.location.href = '/useradmin/profile/changeMyPassword.do?passwordExpired=true&originalUrl=' + relativeUrl;
    }
  },

  /**
   * Sets the passed error onto the view state. The error code maps to i18n key as defined in the template. If the response contains
   * a message key for the error, that will be used instead of the mapped response status message.
   * Also clears any form data.
   *
   * @param response {Object} HTTP response
   * @private
   * @method _processAuthErrors
   */
  _processAuthErrors: function(response) {
    var responseStatus = response && response.status;
    var errors = this._errorCodeMap[responseStatus];
    if (_.isFunction(errors)) {
      var errorFn = _.bind(errors, this);
      errors = errorFn(response);
    }
    errors = errors || this._errorCodeMap.default;

    if (response && response.responseJSON && !_.isEmpty(response.responseJSON.generalReasons)) {
      var generalReason = response.responseJSON.generalReasons[0];
      errors.specificReasonKey = generalReason.messageKey;
    }
    this.set('errors', errors);
    this._clearFormData();
    this.set('submitting', false);
  }
});

AuthFormView.AUTH_CONTEXTS = AUTH_CONTEXTS;

module.exports = AuthFormView;
